/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, memo, useRef } from 'react';
import { Flex, Stack, Text, Box, Spinner } from '@chakra-ui/react';
import ReactPlayer from 'react-player';

import { fetchAPI } from '@/api/fetch';
import { apiUpdateScreeningQuestionVideoDuration } from '@/api/services/screening.service';
import { ScreeningQuestionVideoStatus } from '@/types/enums';
import { Screening } from '@/types/Screening';
import { useVideoPlayer } from '@/hooks/useVideoPlayer';
import VideoControls from '@/components/media/VideoControls';

interface ScreeningPlaybackSectionProps {
  screening: Screening;
  selectedQuestionIndex: number;
  player: ReturnType<typeof useVideoPlayer>;
}

const ScreeningPlaybackSection = memo(
  ({ screening, selectedQuestionIndex, player }: ScreeningPlaybackSectionProps) => {
    const [selectedQuestion, setSelectedQuestion] = useState(
      screening.questions[selectedQuestionIndex]
    );
    const [userScrolledAway, setUserScrolledAway] = useState(false);
    const transcriptContainerRef = useRef<HTMLDivElement>(null);
    const activeTranscriptRef = useRef<HTMLDivElement>(null);
    const isAutoScrolling = useRef(false);

    useEffect(() => {
      player.reset();

      const selectedQuestion = screening.questions[selectedQuestionIndex];
      setSelectedQuestion(screening.questions[selectedQuestionIndex]);

      if (selectedQuestion.video_duration) {
        player.setDuration(selectedQuestion.video_duration);
        player.setLoaded(selectedQuestion.video_duration);
        player.setHasDurationFromAPI(true);
      }
    }, [selectedQuestionIndex, screening.questions]);

    const updateVideoDuration = useCallback(
      async (duration: number) => {
        if (!player.hasDurationFromAPI) {
          try {
            await fetchAPI({
              request: () =>
                apiUpdateScreeningQuestionVideoDuration({
                  screeningId: screening.screening_id,
                  payload: {
                    question_id: selectedQuestion.question_id,
                    video_duration: duration
                  }
                })
            });
          } catch (error) {
            console.error('Failed to update video duration:', error);
          }
        }
      },
      [screening.screening_id, selectedQuestion.question_id, player.hasDurationFromAPI]
    );

    const handleVideoEnded = useCallback(async () => {
      player.handleEnded();
      updateVideoDuration(player.played);
    }, [player, updateVideoDuration]);

    const handleScroll = useCallback(() => {
      if (!isAutoScrolling.current) {
        setUserScrolledAway(true);
      }
    }, []);

    useEffect(() => {
      const container = transcriptContainerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }, [handleScroll]);

    useEffect(() => {
      if (
        activeTranscriptRef.current &&
        transcriptContainerRef.current &&
        player.playing &&
        !userScrolledAway
      ) {
        isAutoScrolling.current = true;
        activeTranscriptRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setTimeout(() => {
          isAutoScrolling.current = false;
        }, 500);
      }
    }, [player.played, player.playing, userScrolledAway]);

    return (
      <Flex gap="24px" w="100%">
        <Stack flex="1" gap="24px">
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontSize="24px" fontWeight="bold" color="neutrals.900">
              {screening.session_name}
            </Text>
            <Text fontSize="14px" color="neutrals.500">
              {selectedQuestion.retake_times}/{screening.retake_limit} Retakes
            </Text>
          </Flex>

          {selectedQuestion && (
            <Box>
              <Text fontSize="18px" fontWeight="semibold" color="neutrals.900" mb="8px">
                Q: {selectedQuestion.question}
              </Text>
            </Box>
          )}

          {selectedQuestion &&
          selectedQuestion.video_status === ScreeningQuestionVideoStatus.Success ? (
            <Stack gap="16px" flex="1" w="100%">
              <Flex gap="32px" align="flex-start" w="100%">
                <Box
                  flex="2"
                  w="100%"
                  h="100%"
                  bg="black"
                  borderRadius="12px"
                  overflow="hidden"
                  position="relative"
                  maxW="600px"
                  minH="290px"
                  maxH="300px"
                >
                  {!player.isReady && (
                    <Flex
                      position="absolute"
                      top="0"
                      left="0"
                      width="100%"
                      height="100%"
                      justifyContent="center"
                      alignItems="center"
                      bg="blackAlpha.700"
                      zIndex={1}
                    >
                      <Spinner size="xl" color="white" />
                    </Flex>
                  )}
                  <ReactPlayer
                    url={selectedQuestion.answer_video_url || ''}
                    playing={player.playing}
                    volume={1}
                    width="100%"
                    height="100%"
                    onPlay={() => {
                      player.setPlaying(true);
                    }}
                    onPause={() => {
                      player.setPlaying(false);
                    }}
                    onProgress={({ playedSeconds, loadedSeconds }) => {
                      player.setPlayed(playedSeconds);

                      if (!player.hasDurationFromAPI) {
                        player.setLoaded(loadedSeconds);
                        player.setDuration(loadedSeconds + 10);
                      }
                    }}
                    onDuration={(duration) => {
                      if (!player.hasDurationFromAPI && duration !== Infinity) {
                        player.setLoaded(duration);
                        player.setDuration(duration);
                        player.setHasDurationFromAPI(true);
                      }
                    }}
                    onReady={() => player.setIsReady(true)}
                    onEnded={handleVideoEnded}
                    onError={player.handleError}
                    ref={player.playerRef}
                    config={{
                      file: {
                        forceVideo: true,
                        forceAudio: true
                      }
                    }}
                  />
                </Box>
                <Stack
                  flex="1"
                  gap="16px"
                  maxH="300px"
                  overflowY="auto"
                  ref={transcriptContainerRef}
                >
                  {selectedQuestion.answer_transcripts.map((message, index) => {
                    const startTime =
                      typeof message.start_time === 'number' ? message.start_time : 0;
                    const nextMessage = selectedQuestion.answer_transcripts[index + 1];
                    const endTime = nextMessage
                      ? typeof nextMessage.start_time === 'number'
                        ? nextMessage.start_time
                        : startTime + 5
                      : startTime + 5;

                    const isActive = player.played >= startTime && player.played < endTime;

                    return (
                      <Flex
                        key={index}
                        gap="12px"
                        alignItems="flex-start"
                        bg={isActive ? 'blue.50' : 'transparent'}
                        p="8px"
                        borderRadius={isActive ? '8px' : '0'}
                        transition="all 0.2s ease"
                        ref={isActive ? activeTranscriptRef : null}
                      >
                        <Stack gap="4px" flex="1">
                          <Flex alignItems="center" gap="8px">
                            <Text
                              fontSize="14px"
                              color={isActive ? 'blue.600' : 'neutrals.500'}
                              fontWeight={isActive ? 'semibold' : 'normal'}
                            >
                              {typeof message.start_time === 'number'
                                ? new Date(message.start_time * 1000)
                                    .toISOString()
                                    .substring(14, 19)
                                : message.start_time}
                            </Text>
                          </Flex>
                          <Text
                            fontSize="16px"
                            color={isActive ? 'neutrals.800' : 'neutrals.700'}
                            lineHeight="1.5"
                            fontWeight={isActive ? 'medium' : 'normal'}
                          >
                            {message.text}
                          </Text>
                        </Stack>
                      </Flex>
                    );
                  })}
                </Stack>
              </Flex>

              <Box
                w="100%"
                mt="16px"
                border="1px solid"
                borderColor="neutrals.200"
                borderRadius="12px"
                p="16px"
              >
                {player.error ? (
                  <Text color="red.500" textAlign="center">
                    {player.error}
                  </Text>
                ) : (
                  <VideoControls
                    playing={player.playing}
                    onPlayPause={() => {
                      player.setPlaying(!player.playing);
                    }}
                    onSeek={player.handleSeek}
                    played={player.played}
                    duration={player.duration}
                    loaded={player.loaded}
                    smoothProgress={player.smoothProgress}
                  />
                )}
              </Box>
            </Stack>
          ) : (
            <Text color="neutrals.500" textAlign="center" mt="120px" fontSize="16px">
              No video available for this question.
            </Text>
          )}
        </Stack>
      </Flex>
    );
  }
);

ScreeningPlaybackSection.displayName = 'ScreeningPlaybackSection';

export default ScreeningPlaybackSection;
